import { useCallback } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import {
  ADD_FACEBOOK_CONNECTOR,
  ADD_GOOGLE_ADS_CONNECTOR,
  ADD_GOOGLE_ANALYTICS_CONNECTOR,
  ADD_GOOGLE_SEARCH_CONSOLE_CONNECTOR,
  ADD_SHOPIFY_CONNECTOR,
} from 'src/utils/gql/mutations';
import { GET_CONNECTORS } from 'src/utils/gql/queries';
import {
  ConnectorType,
  Mutation,
  MutationAddFacebookConnectorArgs,
  MutationAddGoogleAdsConnectorArgs,
  MutationAddGoogleAnalyticsConnectorArgs,
  MutationAddGoogleSearchConsoleConnectorArgs,
  MutationAddShopifyConnectorArgs,
} from 'src/utils/gql/types';

interface UseAddConnectorProps {
  type: ConnectorType;
  code: string;
}

export const useAddConnector = ({ type, code }: UseAddConnectorProps) => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message = graphQLErrors.length ? graphQLErrors[0].message : errorText;

    snackbar(message);
  };

  const [addFacebookConnector, { loading: addingFacebookConnector }] =
    useMutation<
      Pick<Mutation, 'addFacebookConnector'>,
      MutationAddFacebookConnectorArgs
    >(ADD_FACEBOOK_CONNECTOR, {
      refetchQueries: [{ query: GET_CONNECTORS }],
      onCompleted: () => {
        navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
        snackbar(
          'Facebook was successfully added.',
          'success',
          ConnectorType.FACEBOOK,
        );
      },
      onError,
    });

  const [addGoogleAdsConnector, { loading: addingGoogleAdsConnector }] =
    useMutation<
      Pick<Mutation, 'addGoogleAdsConnector'>,
      MutationAddGoogleAdsConnectorArgs
    >(ADD_GOOGLE_ADS_CONNECTOR, {
      refetchQueries: [{ query: GET_CONNECTORS }],
      onCompleted: () => {
        navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
        snackbar(
          'Google Ads was successfully added.',
          'success',
          ConnectorType.GOOGLE_ADS,
        );
      },
      onError,
    });

  const [
    addGoogleAnalyticsConnector,
    { loading: addingGoogleAnalyticsConnector },
  ] = useMutation<
    Pick<Mutation, 'addGoogleAnalyticsConnector'>,
    MutationAddGoogleAnalyticsConnectorArgs
  >(ADD_GOOGLE_ANALYTICS_CONNECTOR, {
    refetchQueries: [{ query: GET_CONNECTORS }],
    onCompleted: () => {
      navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
      snackbar(
        'Google Analytics 4 was successfully added.',
        'success',
        ConnectorType.GOOGlE_ANALYTICS4,
      );
    },
    onError,
  });

  const [
    addGoogleSearchConsoleConnector,
    { loading: addingGoogleSearchConsoleConnector },
  ] = useMutation<
    Pick<Mutation, 'addGoogleSearchConsoleConnector'>,
    MutationAddGoogleSearchConsoleConnectorArgs
  >(ADD_GOOGLE_SEARCH_CONSOLE_CONNECTOR, {
    refetchQueries: [{ query: GET_CONNECTORS }],
    onCompleted: () => {
      navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
      snackbar(
        'Google Search Console was successfully added.',
        'success',
        ConnectorType.GOOGLE_SEARCH_CONSOLE,
      );
    },
    onError,
  });

  const [addShopifyConnector, { loading: addingShopifyConnector }] =
    useMutation<
      Pick<Mutation, 'addShopifyConnector'>,
      MutationAddShopifyConnectorArgs
    >(ADD_SHOPIFY_CONNECTOR, {
      refetchQueries: [{ query: GET_CONNECTORS }],
      onCompleted: () => {
        navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
        snackbar(
          'Shopify was successfully added.',
          'success',
          ConnectorType.SHOPIFY,
        );
      },
      onError,
    });

  let buttonLoading = false;

  switch (type) {
    case ConnectorType.FACEBOOK:
      buttonLoading = addingFacebookConnector;

      break;
    case ConnectorType.GOOGLE_ADS:
      buttonLoading = addingGoogleAdsConnector;

      break;
    case ConnectorType.GOOGlE_ANALYTICS4:
      buttonLoading = addingGoogleAnalyticsConnector;

      break;
    case ConnectorType.GOOGLE_SEARCH_CONSOLE:
      buttonLoading = addingGoogleSearchConsoleConnector;

      break;
    case ConnectorType.SHOPIFY:
      buttonLoading = addingShopifyConnector;

      break;
  }

  const handleAddConnector = useCallback(() => {
    switch (type) {
      case ConnectorType.FACEBOOK:
        return addFacebookConnector({ variables: { code } });
      case ConnectorType.GOOGLE_ADS:
        return addGoogleAdsConnector({ variables: { code } });
      case ConnectorType.GOOGlE_ANALYTICS4:
        return addGoogleAnalyticsConnector({ variables: { code } });
      case ConnectorType.GOOGLE_SEARCH_CONSOLE:
        return addGoogleSearchConsoleConnector({ variables: { code } });
      case ConnectorType.SHOPIFY:
        return addShopifyConnector({ variables: { code } });
      default:
        return null;
    }
  }, [
    addFacebookConnector,
    addGoogleAdsConnector,
    addGoogleAnalyticsConnector,
    addGoogleSearchConsoleConnector,
    addShopifyConnector,
    code,
    type,
  ]);

  return {
    buttonLoading,
    handleAddConnector,
  };
};
